import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { CopilotNavigationHint, TransformerSavedEvent } from '../models/copilot.models';

export interface CopilotContext {
  transformType: number;
  transformId: string;
  refineryId: string;
  transformLabel: string;
  transformName: string;
  currentConfig: any;
  columnNames: string[];
  /**
   * Cockpit constraints — the complete set of valid values the UI exposes for this transformer.
   * Populated by getCopilotConstraints() on each transformer subclass.
   * Includes valid enum options, valid source/target columns, defaults, and any
   * other restriction the UI enforces. The Copilot must stay within these bounds.
   */
  constraints: any;
  inputCacheKey?: string;   // "{nodeId}-{farmGuid}" — set after downloadInputDataAsync completes
  lastRunStats?: {
    globalStat?: { rowHits: number; rowMisses: number; hitRate: number };
    ruleStats?:  { ruleIndex: number; rowHits: number }[];
    totalRows?:  number;
  };
}

export interface CopilotConfigEvent {
  mode: 'replace' | 'merge' | 'preview';
  config: any;
  transformId: string;
  /** True when this push is a still-staging batch rule, not a validated result — the
   *  receiving component should update its display but must not launch a live DTR run. */
  skipRun?: boolean;
}

@Injectable({ providedIn: 'root' })
export class CopilotContextService {
  /** Current transformer context — null when no transformer is open. */
  context$ = new BehaviorSubject<CopilotContext | null>(null);

  /** Registered by the active transformer component. Always returns the live View Config JSON. */
  getLiveConfig: (() => any) | null = null;

  /** Registered by the active transformer component.
   *  Runs the DTR with runAll = true and resolves when complete. */
  runForAnalysis: (() => Promise<void>) | null = null;

  /** Emitted when the copilot proposes a config change the transformer must apply. */
  configEvent$ = new Subject<CopilotConfigEvent>();

  /** Emitted by the transformer component after a successful Save Transformer. */
  transformerSaved$ = new Subject<TransformerSavedEvent>();

  /** Emitted immediately after a DTR run completes with fresh stats. Used to signal
   *  the Copilot build waiter without relying on Angular CD timing. */
  runStatsReady$ = new Subject<any>();

  /** Emitted by the Copilot advisor when a sample range change is recommended and accepted. */
  sampleRangeUpdate$ = new Subject<{ sampleStart: number; sampleEnd: number; sortBy?: string }>();

  /** Last Smart Sample range applied, keyed by transformId. Survives component navigation. */
  private _lastSampleRange: { sampleStart: number; sampleEnd: number; sortBy?: string } | null = null;
  private _lastSampleRangeTransformId: string | null = null;

  /** Apply a Smart Sample range — stores it for navigation-back restoration and emits to subscribers. */
  applySampleRange(update: { sampleStart: number; sampleEnd: number; sortBy?: string }): void {
    this._lastSampleRange = update;
    this._lastSampleRangeTransformId = this.context$.value?.transformId ?? null;
    this.sampleRangeUpdate$.next(update);
  }

  /** Returns the last Smart Sample range if it was applied to the given transformer, else null. */
  getRestoredSampleRange(transformId: string): { sampleStart: number; sampleEnd: number; sortBy?: string } | null {
    return this._lastSampleRangeTransformId === transformId ? this._lastSampleRange : null;
  }

  /** Emitted by the transformer component after a Copilot-triggered DTR run completes.
   *  Value is elapsed milliseconds. Copilot panel appends the time to the last message. */
  dtrRunMs$ = new Subject<number>();

  /** Emitted when Flask re-sorts rules by actual hit counts after a run.
   *  Transformer components apply the new rule order without triggering a DTR run. */
  rulesSorted$ = new Subject<{ rules: any[]; transformId: string; userInitiated?: boolean }>();

  /** Emitted when Copilot suggests navigating to a UI view (e.g. Summary Fields).
   *  Subscribed by the layout component that owns the tab group. */
  navigateTo$ = new Subject<CopilotNavigationHint>();

  set(ctx: CopilotContext): void {
    this.context$.next(ctx);
  }

  update(partial: Partial<CopilotContext>): void {
    const current = this.context$.value;
    if (current) {
      this.context$.next({ ...current, ...partial });
    }
  }

  clear(): void {
    this.context$.next(null);
  }

  emitConfigReplace(config: any, transformId: string): void {
    this.configEvent$.next({ mode: 'replace', config, transformId });
  }

  emitConfigMerge(config: any, transformId: string): void {
    this.configEvent$.next({ mode: 'merge', config, transformId });
  }

  emitConfigPreview(config: any, transformId: string, skipRun: boolean = false): void {
    this.configEvent$.next({ mode: 'preview', config, transformId, skipRun });
  }

  /** Emitted when Copilot pushes a structured data grid (e.g. contamination rows preview). */
  analysisPreview$ = new Subject<any>();

  emitAnalysisPreview(data: any): void {
    this.analysisPreview$.next(data);
  }

  /** Emitted when Copilot computes a config score after a rule change. */
  bpmScore$ = new Subject<{ general: { score: number; max_auto: number }; domain: { name: string; score: number; max_auto: number; max_total: number } | null; }>();

  emitBpmScore(data: { general: { score: number; max_auto: number }; domain: { name: string; score: number; max_auto: number; max_total: number } | null; }): void {
    this.bpmScore$.next(data);
  }

  emitTransformerSaved(event: TransformerSavedEvent): void {
    this.transformerSaved$.next(event);
  }

  /** Emitted when the Copilot panel "Run Audit" button is clicked (Flavor 1). */
  runAudit$ = new Subject<void>();

  emitRunAudit(): void {
    this.runAudit$.next();
  }

  /** Emitted when the Analyst resets a transformer to its blank/default state via the
   *  top header "Reset Transformer" button — the Copilot panel must drop its session
   *  rather than keep a conversation that refers to a config that no longer exists. */
  resetTransformer$ = new Subject<void>();

  emitResetTransformer(): void {
    this.resetTransformer$.next();
  }
}
